import * as React from 'react';
import { useState, useEffect } from 'react';
import {
	Box,
	TextField,
	// Modal,
	Typography,
	IconButton,
	// Card,
	// CardContent,
	// Table,
	// TableBody,
	// TableRow,
	// TableCell,
	// TableHead,
	Container,
	// Button,
} from '@mui/material';
import { GridToolbar, GridColDef, GridValueGetter } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Chip from '@mui/material/Chip';
import { StyledDataGrid } from '../../component/ThemeComponent';
import { getAllOrder } from '../../../services/api/OrderAPI';
import ReuseableTap from '../../../components/common/ReuseableTap';
import { StatusCell } from './StatusCell';

interface OrderItem {
	productId: string;
	quantity: number;
	price: number;
	subtotal: number;
	weight: number;
	name: string;
	category: string;
}

interface ShippingAddress {
	name: string;
	phone: string;
	address: string;
	city: string;
	state: string;
	country: string;
	postalCode: string;
	email: string;
}

interface Order {
	_id: string;
	orderId: string;
	orderStatus: string;
	paymentStatus: string;
	razorpayOrderId: string;
	shiprocketOrderId: string;
	shiprocketReferenceId: string;
	totalAmount: number;
	totalWeight: number;
	createdAt: string;
	updatedAt: string;
	userId: string;
	cancelReason: string | null;
	items: OrderItem[];
	shippingAddress: ShippingAddress;
}
const getStatusColor = (status: string) => {
	switch (status) {
		case 'Paid':
			return 'success'; // green
		case 'Unpaid':
			return 'error'; // red
		case 'Pending':
			return 'warning'; // orange
		case 'Processing':
			return 'secondary'; // blue
		case 'Shipped':
			return 'info'; // indigo
		case 'Delivered':
			return 'success'; // green
		case 'Completed':
			return 'success'; // green
		case 'Cancelled':
			return 'error'; // red
		case 'New':
			return 'primary'; // blue
		default:
			return 'default'; // grey
	}
};

const Orders: React.FC = () => {
	const [searchText, setSearchText] = useState<string>('');
	const [open, setOpen] = useState<boolean>(false);
	const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
	const [rows, setRows] = useState<Order[]>([]);
	const slug = localStorage.getItem('slug');
	const location = window.location.pathname.split('/');
	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(event.target.value);
	};

	const handleViewDetails = (order: Order) => {
		setSelectedOrder(order);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const fetchdataorder = async () => {
		const response = await getAllOrder();
		if (response) {
			const data = response?.data?.orders?.map((order: Order) => ({
				...order,
				id: order._id, // Add id property
			}));
			setRows(data);
		} else {
			console.error('Failed to fetch orders');
		}
	};

	useEffect(() => {
		fetchdataorder();
	}, []);
	useEffect(() => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	}, []);

	const createdAtDate: GridValueGetter<(typeof rows)[number], unknown> = (value, row) =>
		`${
			new Date(row.createdAt)
				.toLocaleString('en-GB', {
					day: '2-digit',
					month: 'short',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					hour12: true,
				})
				.replace(/,/g, '')
				.replace(/(\d+)\/(\d+)\/(\d+),? (\d+:\d+) (AM|PM)/, '$1-$2-$3 $4 $5') || 'N/A'
		}`;

	const updatedAtDate: GridValueGetter<(typeof rows)[number], unknown> = (value, row) =>
		`${
			new Date(row.updatedAt)
				.toLocaleString('en-GB', {
					day: '2-digit',
					month: 'short',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
					hour12: true,
				})
				.replace(/,/g, '')
				.replace(/(\d+)\/(\d+)\/(\d+),? (\d+:\d+) (AM|PM)/, '$1-$2-$3 $4 $5') || 'N/A'
		}`;

	const filteredRows = rows?.filter(
		(row) =>
			row?.orderId?.includes(searchText) ||
			row?.shippingAddress?.email?.toLowerCase()?.includes(searchText?.toLowerCase())
	);

	const columns: GridColDef[] = [
		{ field: 'orderId', headerName: 'Order ID', width: 150 },
		{
			field: 'shippingAddress',
			headerName: 'Phone',
			width: 130,
			valueGetter: (params: ShippingAddress) => (params.phone ? params.phone : ''),
		},
		{
			field: 'items',
			headerName: 'Ordered Items',
			width: 150,
			valueGetter: (params: { row: OrderItem }) =>
				Array.isArray(params) ? params?.map((item: any) => item?.name).join(', ') : '',
		},
		// { field: 'createdAt', headerName: 'Date', width: 180 },
		{
			field: 'createdAt',
			headerName: 'Order Date',
			width: 180,
			type: 'string',
			valueGetter: createdAtDate,
		},
		{
			field: 'updatedAt',
			headerName: 'Update Date',
			width: 180,
			type: 'string',
			valueGetter: updatedAtDate,
		},
		{
			field: 'orderStatus',
			headerName: 'Order Status',
			width: 150,
			renderCell: (params) => <StatusCell params={params} />,
		},
		// {
		// 	field: 'orderStatus',
		// 	headerName: 'Order Status',
		// 	width: 150,
		// 	renderCell: (params) => (
		// 		<Chip
		// 			label={params.value}
		// 			color={getStatusColor(params.value)}
		// 			size="small"
		// 			variant="outlined"
		// 		/>
		// 	),
		// },
		{
			field: 'totalAmount',
			headerName: 'Total Price',
			width: 150,
		},
		{
			field: 'paymentStatus',
			headerName: 'Payment Status',
			width: 150,
			renderCell: (params) => (
				<Chip
					label={params.value}
					color={getStatusColor(params.value)}
					size="small"
					variant="outlined"
				/>
			),
		},
		{
			field: 'actions',
			headerName: 'Actions',
			width: 100,
			renderCell: (params) => (
				<IconButton onClick={() => handleViewDetails(params.row)}>
					<VisibilityIcon />
				</IconButton>
			),
		},
	];

	return (
		<Container>
			<Box sx={{ width: '100%' }}>
				<div
					className="d-flex"
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
					}}
				>
					<div>
						{!location.includes(slug as string) && (
							<Typography
								gutterBottom
								style={{
									fontFamily: 'Poppins',
									fontWeight: '700',
									fontSize: '24px',
								}}
							>
								Orders
							</Typography>
						)}
						<span
							style={{
								fontFamily: 'Poppins',
								fontWeight: '400',
								fontSize: '14px',
								color: '#006D48',
							}}
						>
							Manage your Orders list, new Orders, and more.
						</span>
					</div>
				</div>
				<Grid container spacing={2} sx={{ mb: 2, mt: 2 }}>
					<Grid size={{ xs: 12, sm: 12, md: 12 }}>
						<TextField
							label="Search"
							variant="outlined"
							value={searchText}
							onChange={handleSearch}
							fullWidth
							style={{ fontFamily: 'Poppins' }}
							size="small"
						/>
					</Grid>
				</Grid>
				<Box sx={{ height: 600, width: 'auto' }}>
					<StyledDataGrid
						rows={filteredRows}
						columns={columns}
						initialState={{
							pagination: {
								paginationModel: { pageSize: 10 },
							},
						}}
						pageSizeOptions={[10]}
						slots={{ toolbar: GridToolbar }}
						getRowId={(row) => row?._id} // Specify custom id
					/>
				</Box>

				{selectedOrder && (
					<ReuseableTap
						open={open}
						selectedOrder={selectedOrder}
						handleClose={handleClose}
					/>
				)}
			</Box>
		</Container>
	);
};

export default Orders;
